import { z } from "zod";
import { normalizeDocName } from "./document-validity";

export const notificationChannelsSchema = z.object({
  pushNotifications: z.boolean().optional(),
  emailNotifications: z.boolean().optional(),
  smsNotifications: z.boolean().optional(),
});
export type NotificationChannels = z.infer<typeof notificationChannelsSchema>;

export const notificationPreferencesSchema = z.object({
  notification: notificationChannelsSchema.optional(),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/**
 * Stable notification type keys. Clients translate `notifications.<type>.*`
 * with the params below; stored title/content remain the fallback for legacy
 * rows and for clients without the key.
 */
export const NotificationTypeValues = [
  "DOCUMENT_UPLOADED",
  "DOCUMENT_APPROVED",
  "DOCUMENT_REJECTED",
  /** Approved document close to expiry (J-30 push, J-14 email, J-7 SMS). */
  "DOCUMENT_EXPIRING",
  /** Approved document expired: the driver is paused until the renewal is approved. */
  "DOCUMENT_EXPIRED_PAUSED",
  /** Renewal approved: the expiry pause was lifted. */
  "DOCUMENT_EXPIRY_PAUSE_LIFTED",
  /** Periodic reminder: documents the driver still has to send (missing, rejected, expired). */
  "DOCUMENTS_INCOMPLETE",
] as const;
export const notificationTypeSchema = z.enum(NotificationTypeValues);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const isNotificationType = (value: unknown): value is NotificationType =>
  notificationTypeSchema.safeParse(value).success;

/** Persisted metadata shared by the inbox and push payloads. */
export const notificationMetadataSchema = z
  .object({
    /** Unknown future types must not break parsing of older clients. */
    type: z.string().optional(),
    uploaderName: z.string().nullable().optional(),
    uploaderType: z.string().nullable().optional(),
    documentLabel: z.string().optional(),
    rejectionReason: z.string().nullable().optional(),
    driverName: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
    relatedType: z.string().optional(),
    relatedId: z.union([z.string(), z.number()]).nullable().optional(),
    purpose: z.string().optional(),
    documentId: z.union([z.string(), z.number()]).optional(),
    document: z.string().optional(),
    documentType: z.string().optional(),
    deliveryStatus: z.string().nullable().optional(),
    relatedStatus: z.string().optional(),
  })
  .catchall(z.json());
export type NotificationMetadata = z.infer<typeof notificationMetadataSchema>;

export interface NotificationTranslation {
  type: NotificationType;
  /** i18n key prefix: `${key}.title` / `${key}.body`. */
  key: `notifications.${NotificationType}`;
  /** Raw document key (e.g. `kbisPath`), normalized for label lookups. */
  documentKey?: string;
  params: {
    document: string;
    uploaderName: string;
    uploaderType: string;
    rejectionReason: string;
    driverName: string;
    /** ISO date; clients format it in their locale. */
    expiresAt: string;
  };
}

/**
 * Resolves the typed translation of a notification, or null for legacy rows
 * (no/unknown `type`) which must fall back to the stored title/content.
 */
export function getNotificationTranslation(
  metadata: Record<string, unknown> | null | undefined
): NotificationTranslation | null {
  const type = metadata?.type;
  if (!isNotificationType(type)) return null;
  const text = (value: unknown) => (typeof value === "string" ? value : "");
  const document = text(metadata?.document);
  return {
    type,
    key: `notifications.${type}`,
    documentKey: document ? normalizeDocName(document) : undefined,
    params: {
      document: text(metadata?.documentLabel) || document,
      uploaderName: text(metadata?.uploaderName),
      uploaderType: text(metadata?.uploaderType),
      rejectionReason: text(metadata?.rejectionReason),
      driverName: text(metadata?.driverName),
      expiresAt: text(metadata?.expiresAt),
    },
  };
}

const ADMIN_DRIVERS_TABLE = "/admin/drivers/drivers-table";

/** Admin driver table per `DriverStatus`: the driver's real state decides the page. */
const ADMIN_DRIVER_STATUS_TABLE: Record<string, string> = {
  PENDING: "waiting-requests",
  ACCEPTED: "accepted-requests",
  REJECTED: "refused-requests",
  DISABLED: "disabled-drivers",
};

/** Fallback for rows without `relatedStatus` (older notifications). */
const ADMIN_DRIVER_PURPOSE_TABLE: Record<string, string> = {
  DRIVER_DOCUMENT_INITIAL_UPLOAD: "waiting-requests",
  DRIVER_DOCUMENT_RENEWAL_UPLOAD: "accepted-requests",
  ACCEPTED_DRIVER_DOCUMENT_UPLOAD: "accepted-requests",
  // Emitted by older clients; removed from the shared upload-purpose contract.
  DRIVER_APPLICATION_DOCUMENT_UPLOAD: "waiting-requests",
};

export interface AdminNotificationTarget {
  relatedType?: unknown;
  relatedId?: unknown;
  relatedStatus?: unknown;
  purpose?: unknown;
  deliveryStatus?: unknown;
  document?: unknown;
}

const nonEmpty = (value: unknown): string | undefined =>
  typeof value === "string" && value !== ""
    ? value
    : typeof value === "number"
      ? String(value)
      : undefined;

/**
 * Single source of truth for where an admin lands when opening a notification.
 * Used by the in-app list, the foreground toast and (through the FCM `link`
 * computed by the API) the service worker's `notificationclick` handler.
 */
export function getAdminNotificationPath(target: AdminNotificationTarget): string | undefined {
  const relatedType = nonEmpty(target.relatedType);
  const relatedId = nonEmpty(target.relatedId);
  if (!relatedId) return undefined;

  if (relatedType === "DRIVER") {
    const status = nonEmpty(target.relatedStatus);
    const purpose = nonEmpty(target.purpose);
    const table =
      (status && ADMIN_DRIVER_STATUS_TABLE[status]) ||
      (purpose && ADMIN_DRIVER_PURPOSE_TABLE[purpose]);
    if (!table) return undefined;
    const document = nonEmpty(target.document);
    const query = document ? `?document=${encodeURIComponent(document)}` : "";
    return `${ADMIN_DRIVERS_TABLE}/${table}/${encodeURIComponent(relatedId)}${query}`;
  }

  const deliveryStatus = nonEmpty(target.deliveryStatus);
  if (relatedType === "DELIVERY" && deliveryStatus) {
    return `/admin/pilot/delivery-table/${deliveryStatus.toLowerCase().replace(/_/g, "-")}/${relatedId}`;
  }
  return undefined;
}
